import { WorkflowWritebackMergeAuthMethod } from 'libs/end-to-end/src/types/databases/workflows/workflow-writeback-auth-method';
import { z } from 'zod';

/**
 * Connection auth for ChartHop write backs. The org installs a ChartHop
 * connection, and execution resolves the API token from that installation.
 */
export const WriteBackChartHopConnectionMethod = z.object({
  type: z.literal('connection'),
});

export type WriteBackChartHopConnectionMethod = z.infer<
  typeof WriteBackChartHopConnectionMethod
>;

/**
 * The authentication method for writing back promotion readiness scores to ChartHop.
 */
export const WriteBackChartHopAuthMethod = z.union([
  WorkflowWritebackMergeAuthMethod,
  WriteBackChartHopConnectionMethod,
]);

export type WriteBackChartHopAuthMethod = z.infer<
  typeof WriteBackChartHopAuthMethod
>;

/**
 * The place to write promotion readiness scores to in ChartHop.
 */
export const WriteBackChartHopPlaceToWrite = z.object({
  type: z.literal('customFields'),
  // Org ID represents the id or slug that can be used as a path parameters
  // to Chart Hop API's. This can be found either through merge, or a sample request
  // from the customer.
  orgId: z.string().optional(),
  // This represents the custom field name where a readiness score should go.
  // For Chart Hop, a custom field needs to be created with type INTEGER.
  customFieldNameForPromotionReadinessScore: z.string().optional(),
  // This represents the custom field name where a job target should be written that corresponds
  // to a readiness score. For Chart Hop, a custom field needs to be created with type STRING.
  customFieldNameForPromotionReadinessScoreJobGroup: z.string().optional(),
  // This represents the custom field name where a readiness explanation should go.
  // For Chart Hop, a custom field needs to be created with type STRING.
  customFieldNameForPromotionReadinessExplanation: z.string().optional(),
  // This represents the custom field name where a job target should be written that corresponds
  // to a readiness explanation. For Chart Hop, a custom field needs to be created with type STRING.
  customFieldNameForPromotionReadinessExplanationJobGroup: z
    .string()
    .optional(),
});

export type WriteBackChartHopPlaceToWrite = z.infer<
  typeof WriteBackChartHopPlaceToWrite
>;

/**
 * The strategy for writing back promotion readiness scores to ChartHop.
 */
export const WriteBackChartHopStrategy = z.object({
  type: z.literal('chartHop'),

  /**
   * The authentication method.
   */
  authMethod: WriteBackChartHopAuthMethod.optional(),

  /**
   * The places to write.
   */
  placesToWrite: z.array(WriteBackChartHopPlaceToWrite).optional(),
});

export type WriteBackChartHopStrategy = z.infer<
  typeof WriteBackChartHopStrategy
>;
